import { useEffect, useState } from 'react';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import { useDispatch, useSelector } from 'react-redux';
import { updateGradebook } from '../../actions/gradebook';
import { setUserLmsId } from '../../actions/user';
import { extractSettingAs } from '@macmillan-learning/gradebook-shared';

const getShowLinkReefDialog = (state) =>
  get(state, 'gradebook.showLinkReefDialog', false);

const getShowUnlinkReefDialog = (state) =>
  get(state, 'gradebook.showUnlinkReefDialog', false);

const getShowGradebookCommentDialog = (state) =>
  get(state, 'gradebook.showGradebookCommentDialog', false);

const getShowGradebookExportDialog = (state) =>
  get(state, 'gradebook.showGradebookExportDialog', false);

const getUser = (state) => get(state, 'gradebook.user', {});
const getSettings = (state) => get(state, 'gradebook.settings', {});

const getStateLinkReefDialog = (newValue) => ({
  showLinkReefDialog: newValue,
  reefMenuOpen: false,
});

const getStateUnlinkReefDialog = (newValue) => ({
  showUnlinkReefDialog: newValue,
  reefMenuOpen: false,
});

const getStateGradebookCommentDialog = (newValue, gradeCommentData) => ({
  showGradebookCommentDialog: newValue,
  gradeCommentData,
});

const getDataGradebookCommentDialog = (state) =>
  get(state, 'gradebook.gradeCommentData', {});

const getshowIndividualStudentGradebook = (state) =>
  get(state, 'gradebook.showIndividualStudentGradebook', {});

const useModalStates = () => {
  const dispatch = useDispatch();
  const showLinkReefDialog = useSelector(getShowLinkReefDialog);
  const showUnlinkReefDialog = useSelector(getShowUnlinkReefDialog);
  const showGradebookCommentDialog = useSelector(getShowGradebookCommentDialog);
  const showGradebookExportDialog = useSelector(getShowGradebookExportDialog);
  const dataGradebookCommentDialog = useSelector(getDataGradebookCommentDialog);
  const showIndividualStudentGradebook = useSelector(
    getshowIndividualStudentGradebook,
  );
  const user = useSelector(getUser);
  const settings = useSelector(getSettings);
  const [studentIDsState, setStudentIDsState] = useState({
    show: false,
    isEditing: false,
  });

  /**
   * functions to handle StateLinkReefDialog Modal
   */
  const toggleLinkReefDialog = () =>
    dispatch(updateGradebook(getStateLinkReefDialog(!showLinkReefDialog)));
  const closeLinkReefDialog = () =>
    dispatch(updateGradebook(getStateLinkReefDialog(false)));
  /**
   * functions to handle UnlinkReefDialog Modal
   */
  const toggleUnlinkReefDialog = () =>
    dispatch(updateGradebook(getStateUnlinkReefDialog(!showUnlinkReefDialog)));
  const closeUnlinkReefDialog = () =>
    dispatch(updateGradebook(getStateUnlinkReefDialog(false)));
  /**
   * functions to handle GradebookCommentDialog Modal
   */
  const openGradebookCommentDialog = (gradeCommentData) =>
    dispatch(
      updateGradebook(getStateGradebookCommentDialog(true, gradeCommentData)),
    );
  const closeGradebookCommentDialog = () =>
    dispatch(updateGradebook(getStateGradebookCommentDialog(false, {})));

  /**
   * functions to handle GradebookCommentDialog Modal
   */
  const toggleGradebookExportDialog = () =>
    dispatch(
      updateGradebook({
        open: false,
        showGradebookExportDialog: !showGradebookExportDialog,
      }),
    );

  /**
   * function that allows open the StudentIDs modal
   * @param showEditing allow open the modal but the flag showEditing as true
   */
  const openStudentIDs = (showEditing) => {
    const newState = { show: true };

    if (showEditing) {
      newState.isEditing = true;
    }
    setStudentIDsState({
      ...studentIDsState,
      ...newState,
    });
  };

  const closeStudentIDs = () =>
    setStudentIDsState({
      isEditing: false,
      show: false,
    });

  const submitLmsId = (value, user) => {
    dispatch(setUserLmsId({ value, id: user.user_id }));
  };

  const role = user.role;
  const thirdPartyFromUser = user.thirdPartyUserId;
  const thirdPartyFromMetadata = get(
    user,
    'user_metadata.thirdPartyUserId',
    null,
  );
  const requireStudentId = extractSettingAs(
    settings,
    'requireStudentId',
    ({ value }) => value,
  );
  const isStudentIdRequired = requireStudentId === 'true';

  useEffect(() => {
    const isStudent = role === 'student';

    if (isStudentIdRequired && isStudent) {
      let thirdPartyUserId = thirdPartyFromUser || thirdPartyFromMetadata;
      if (
        !thirdPartyUserId ||
        (isArray(thirdPartyUserId) && !thirdPartyUserId.length)
      ) {
        openStudentIDs();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudentIdRequired, role, thirdPartyFromUser, thirdPartyFromMetadata]);

  return {
    showLinkReefDialog,
    showUnlinkReefDialog,
    showGradebookCommentDialog,
    showIndividualStudentGradebook,
    showStudentIDModal: studentIDsState.show,
    showGradebookExportDialog,
    exportDialog: {
      toggle: toggleGradebookExportDialog,
      isShowing: showGradebookExportDialog,
    },
    linkReefDialog: {
      toggle: toggleLinkReefDialog,
      close: closeLinkReefDialog,
    },
    unlinkReefDialog: {
      toggle: toggleUnlinkReefDialog,
      close: closeUnlinkReefDialog,
    },
    gradebookCommentDialog: {
      open: openGradebookCommentDialog,
      close: closeGradebookCommentDialog,
      data: dataGradebookCommentDialog,
    },
    studentID: {
      open: openStudentIDs,
      close: closeStudentIDs,
      submit: submitLmsId,
      isEditing: studentIDsState.isEditing,
    },
    dispatch,
  };
};

export default useModalStates;
